import type { ToolbarProps, View } from 'react-big-calendar'
import React, { useEffect, useMemo, useRef } from 'react'
import { makeStyles, Typography, Button, Hidden, IconButton, Skeleton, Spinner, useIsMobile } from '@paintscout/ui'
import { SingleDatePicker, useUsers, useUserPermissions } from '@paintscout/ui-business'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import classnames from 'classnames'
import moment from 'moment'
import type { CalendarEvent } from 'paintscout'

const SPACE_BETWEEN = 'space-between' as const

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: SPACE_BETWEEN,
    padding: theme.spacing(1.5, 2, 1, 2),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start'
    }
  },
  mobileAvatarRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    overflowX: 'auto',
    overflowY: 'hidden',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    backgroundColor: theme.palette.common.white
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  todayGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  mapNavLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0
  },
  mapNavRight: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    maxWidth: 360,
    overflowX: 'auto',
    overflowY: 'hidden',
    // Right-align via auto margin instead of justify-content: flex-end so
    // overflow scroll works — flex-end traps the overflow before scrollLeft 0.
    '& > *': {
      marginInlineStart: 'auto'
    },
    [theme.breakpoints.down('xs')]: {
      '& > *': {
        marginInlineStart: 0
      }
    },
    '&::-webkit-scrollbar': {
      height: '4px'
    }
  },
  navigationText: {
    ...theme.typography.body1,
    textTransform: 'none',
    color: theme.paintscout.palette.brand.Neutral.P50,
    [theme.breakpoints.down('xs')]: {
      fontSize: '12px'
    }
  },
  boldNavigationText: {
    fontWeight: 600
  },
  clickableNavigation: {
    cursor: 'pointer',
    '@media (hover: hover)': {
      '&:hover': {
        '& > *': {
          color: `${theme.paintscout.palette.primary} !important`
        }
      }
    }
  },
  dayLabelWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75)
  },
  dayBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '24px',
    padding: theme.spacing(0, 0.75),
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.paintscout.palette.primary,
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    lineHeight: 1
  },
  datePickerWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    flex: '0 0 auto',
    minWidth: 0,
    '& .MuiFormControl-root, & .MuiFormControl-fullWidth': {
      width: 'auto !important',
      maxWidth: 'fit-content !important',
      minWidth: 0,
      flex: '0 0 auto !important'
    },
    '& button': {
      padding: 0,
      margin: 0,
      border: 0,
      background: 'transparent',
      cursor: 'pointer',
      color: 'inherit',
      WebkitTextFillColor: 'inherit'
    }
  },
  dateArrow: {
    display: 'inline-block',
    position: 'relative',
    width: '8px',
    height: '1em',
    margin: theme.spacing(0, 0.75, 0, 0.25),
    verticalAlign: 'middle',
    overflow: 'hidden',
    textIndent: '-9999px',
    color: theme.paintscout.palette.brand.Neutral.P50,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      height: '2px',
      borderRadius: '1px',
      backgroundColor: 'currentColor',
      transform: 'translateY(-50%)',
      textIndent: 0
    }
  },
  mobileNavigation: {
    display: 'flex',
    justifyContent: SPACE_BETWEEN,
    alignItems: 'center',

    '& > div': {
      width: 'fit-content'
    }
  },
  loadingSpinner: {
    marginRight: theme.spacing(2),
    '& div': {
      width: '20px !important',
      height: '20px !important'
    }
  },
  productionNavigation: {
    display: 'flex',
    justifyContent: SPACE_BETWEEN,
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    backgroundColor: theme.palette.common.white,
    // SingleDatePicker wraps its trigger in a FormControl with fullWidth — collapse
    // it to its content so space-between can push the date label flush right.
    '& .MuiFormControl-root, & .MuiFormControl-fullWidth': {
      width: 'auto !important',
      flex: '0 0 auto'
    },
    '& .MuiFormControl-root button': {
      padding: 0,
      margin: 0,
      border: 0,
      background: 'transparent',
      cursor: 'pointer'
    }
  }
}))

// React's onWheel is passive, so preventDefault() is a no-op there — attach a
// native non-passive listener instead so vertical wheel can be consumed and
// re-applied as horizontal scroll on the avatar row.
function useHorizontalWheelScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0 || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
      if (el.scrollWidth <= el.clientWidth) return
      e.preventDefault()
      el.scrollLeft += e.deltaY
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])
  return ref
}

interface CalendarToolbarProps extends ToolbarProps {
  events: CalendarEvent[]
  date: Date
  setDate: (date: Date) => void
  tab: 'calendar' | 'map' | 'schedule'
  view: View
  setView: (view: View) => void
  fetchingIntegrationEvents?: boolean
  loading?: boolean
  hideWeekends?: boolean
  EstimatorSelectComponent: React.ReactNode
  isProduction?: boolean
}

export default function CalendarToolbar(props: CalendarToolbarProps) {
  const {
    view: contextView,
    label,
    events,
    date,
    setDate,
    tab,
    loading,
    fetchingIntegrationEvents,
    hideWeekends,
    EstimatorSelectComponent,
    isProduction
  } = props

  const { users } = useUsers()
  const classes = useStyles()
  const mobile = useIsMobile({ xs: true })
  const { permissionToViewAllEvents } = useUserPermissions()
  const mapTabAvatarRowRef = useHorizontalWheelScroll<HTMLDivElement>()
  const desktopAvatarRowRef = useHorizontalWheelScroll<HTMLDivElement>()
  const mobileAvatarRowRef = useHorizontalWheelScroll<HTMLDivElement>()

  const isProductionMap = isProduction && tab === 'map'
  const view = isProductionMap ? 'day' : contextView
  const showEstimatorSelect = users?.length > 1 && !loading && permissionToViewAllEvents && view !== 'agenda'

  const getMapLabel = () => {
    if (view === 'day') {
      return moment(date).format('MMMM D, YYYY')
    }

    if (view === 'week') {
      const start = moment(date).clone().startOf('week')
      const end = moment(date).clone().endOf('week')
      if (start.month() === end.month()) {
        return `${start.format('MMMM D')} - ${end.format('D')}`
      }
      return `${start.format('MMMM D')} - ${end.format('MMMM D')}`
    }

    return moment(date).format('MMMM YYYY')
  }

  const constructLabel = () => {
    if (tab === 'map') {
      return getMapLabel()
    }

    if (view === 'week') {
      const start = hideWeekends ? moment(date).startOf('week').add(1, 'days') : moment(date).clone().startOf('week')
      const end = hideWeekends ? moment(date).endOf('week').subtract(1, 'days') : moment(date).clone().endOf('week')
      if (start.month() === end.month()) {
        return `${start.format('MMMM D')} - ${end.format('D')}`
      }
      return `${start.format('MMMM D')} - ${end.format('MMMM D')}`
    }

    if (view === 'day') {
      return moment(date).format(mobile ? 'dddd, MMM D' : 'dddd, MMMM D')
    }

    if (view === 'month') {
      return moment(date).format('MMMM YYYY')
    }

    return label
  }

  const renderWords = (label: string) =>
    label.split(' ').map((word, index) => {
      if (word === '-') {
        return (
          <span className={classes.dateArrow} key={index}>
            {word}
          </span>
        )
      }
      const boldWord = word.length > 4 || index === 0 || (index === 1 && view === 'day')
      if (boldWord) {
        return (
          <span className={classnames(classes.navigationText, classes.boldNavigationText)} key={index}>
            {word}{' '}
          </span>
        )
      }
      return (
        <span className={classes.navigationText} key={index}>
          {word}{' '}
        </span>
      )
    })

  const formatLabel = () => {
    if (view === 'day' && tab !== 'map' && moment(date).isSame(moment(), 'day')) {
      const monthLabel = moment(date).format(mobile ? 'dddd, MMM' : 'dddd, MMMM')
      const dayNumber = moment(date).format('D')
      return (
        <span className={classes.dayLabelWrapper}>
          <span>{renderWords(monthLabel)}</span>
          <span className={classes.dayBadge}>{dayNumber}</span>
        </span>
      )
    }

    return renderWords(constructLabel())
  }

  // Map view to valid moment.js units
  const getMomentUnit = (view: string) => {
    switch (view) {
      case 'day':
        return 'day'
      case 'week':
      case 'work_week':
        return 'week'
      case 'month':
        return 'month'
      default:
        return 'day'
    }
  }

  const handleNavigate = (action: string, specificDate?: Date) => {
    const unit = getMomentUnit(view)
    switch (action) {
      case 'PREV':
        setDate(moment(date).subtract(1, unit).toDate())
        break
      case 'NEXT':
        setDate(moment(date).add(1, unit).toDate())
        break
      case 'TODAY':
        setDate(moment().toDate())
        break
      case 'DATE':
        setDate(moment(specificDate).toDate())
        break
      default:
        break
    }
  }

  const datePickerEvents = useMemo(() => events.filter((event) => event.eventType !== '_unavailable_'), [events])

  const renderDatePicker = () => (
    <span className={classes.datePickerWrapper}>
      <SingleDatePicker
        date={moment(date)}
        onChange={(date) => handleNavigate('DATE', date.toDate())}
        events={datePickerEvents}
        customTrigger={
          <button type="button" aria-label="Open date picker">
            <Typography className={classes.clickableNavigation}>{formatLabel()}</Typography>
          </button>
        }
        PopperProps={{
          placement: 'bottom-end',
          // SingleDatePicker defaults to disablePortal: true; override to
          // false so the popover escapes the calendar Card's clipping context.
          disablePortal: false,
          modifiers: {
            flip: { enabled: true },
            preventOverflow: { enabled: true, boundariesElement: 'viewport' }
          }
        }}
        modalBreakpoint={null}
      />
    </span>
  )

  const NavigationComponent = ({ withTodayInside = false }: { withTodayInside?: boolean } = {}) => {
    if (loading && !mobile) {
      return (
        <div className={classes.navigation}>
          <Skeleton variant="rect" width={withTodayInside ? 240 : 160} height={32} />
        </div>
      )
    }

    if (withTodayInside) {
      return (
        <>
          <div className={classes.todayGroup}>
            <IconButton size="small" onClick={() => handleNavigate('PREV')}>
              <ChevronLeftIcon />
            </IconButton>
            <TodayComponent />
            <IconButton size="small" onClick={() => handleNavigate('NEXT')}>
              <ChevronRightIcon />
            </IconButton>
          </div>
          {renderDatePicker()}
        </>
      )
    }

    return (
      <>
        <IconButton size="small" onClick={() => handleNavigate('PREV')}>
          <ChevronLeftIcon />
        </IconButton>
        {renderDatePicker()}
        <IconButton size="small" onClick={() => handleNavigate('NEXT')}>
          <ChevronRightIcon />
        </IconButton>
      </>
    )
  }

  const TodayComponent = () => {
    if (loading) {
      return <Skeleton variant="rect" width={64} height={32} className={classes.clickableNavigation} />
    }
    return (
      <Button
        className={classnames(classes.navigationText, classes.boldNavigationText, classes.clickableNavigation)}
        variant="text"
        size="small"
        onClick={() => handleNavigate('TODAY')}
      >
        Today
      </Button>
    )
  }

  return (
    <>
      {tab === 'map' ? (
        <>
          <div className={classes.root}>
            <div className={classes.mapNavLeft}>
              <Hidden xsDown implementation="js">
                {fetchingIntegrationEvents && <Spinner className={classes.loadingSpinner} />}
                <NavigationComponent withTodayInside />
              </Hidden>
            </div>
            <div ref={mapTabAvatarRowRef} className={classes.mapNavRight}>
              {showEstimatorSelect && EstimatorSelectComponent}
            </div>
          </div>
          {!loading && (
            <Hidden smUp implementation="js">
              <div className={classes.productionNavigation}>
                <NavigationComponent withTodayInside />
              </div>
            </Hidden>
          )}
        </>
      ) : view === 'agenda' && !fetchingIntegrationEvents ? null : (
        <Hidden xsDown implementation="js">
          <div className={classes.root} style={view === 'agenda' ? { borderBottom: 'none' } : undefined}>
            <div className={classes.mapNavLeft}>
              {fetchingIntegrationEvents && <Spinner className={classes.loadingSpinner} />}
              {view !== 'agenda' && <NavigationComponent withTodayInside />}
            </div>
            <div ref={desktopAvatarRowRef} className={classes.mapNavRight}>
              {showEstimatorSelect && EstimatorSelectComponent}
            </div>
          </div>
        </Hidden>
      )}
      {tab !== 'map' && !loading && (
        <Hidden smUp implementation="js">
          {showEstimatorSelect && (
            <div
              ref={mobileAvatarRowRef}
              className={classes.mobileAvatarRow}
              style={(view as string) === 'agenda' ? { borderBottom: 'none' } : undefined}
            >
              {EstimatorSelectComponent}
            </div>
          )}
          {view !== 'agenda' && (
            <div className={classes.productionNavigation}>
              <NavigationComponent withTodayInside />
            </div>
          )}
        </Hidden>
      )}
    </>
  )
}
